import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useEditFlashMessage, useFlashMessages } from '../api';
import type { FlashMessage } from '../types';

export function FlashMessagesPage() {
  const list = useFlashMessages();
  const rows = list.data ?? [];

  const [viewing, setViewing] = useState<FlashMessage | null>(null);

  const columns = useMemo<ColumnDef<FlashMessage>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'moduleName',
        header: 'Module',
        cell: ({ row }) => row.original.moduleName ?? '—',
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 220,
        cell: ({ row }) => row.original.action ?? '—',
      },
      {
        id: 'message',
        header: 'Message',
        size: 120,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.message ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setViewing(row.original)}
            >
              <Eye className="h-3.5 w-3.5" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Announcements"
        description="System-wide banner copy shown by each module — review and edit the text users see."
      />

      <DataTable<FlashMessage, unknown>
        tableId="flash-messages:list:v1"
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        searchPlaceholder="Search by module or action…"
        emptyTitle="No flash messages"
        emptyDescription="Nothing to show — modules have not registered any messages yet."
        getRowId={(row) => String(row.id)}
        pageSize={25}
      />

      <FlashMessageDialog
        row={viewing}
        onClose={() => setViewing(null)}
      />
    </FillPage>
  );
}

function FlashMessageDialog({
  row,
  onClose,
}: {
  row: FlashMessage | null;
  onClose: () => void;
}) {
  const edit = useEditFlashMessage();
  const [editable, setEditable] = useState(false);
  const [draft, setDraft] = useState('');

  // Sync draft + reset edit mode whenever a new row is opened.
  useEffect(() => {
    if (row) {
      setDraft(row.message ?? '');
      setEditable(false);
    }
  }, [row]);

  const handleSave = () => {
    if (!row) return;
    const trimmed = draft.trim();
    if (!trimmed) {
      toast.error('Message cannot be empty.');
      return;
    }
    edit.mutate(
      { id: row.id, action: row.action ?? '', message: trimmed },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Message updated.');
          onClose();
        },
        onError: () => toast.error('Could not save the message.'),
      },
    );
  };

  const unchanged = draft.trim() === (row?.message ?? '').trim();

  return (
    <Dialog open={row !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {row?.moduleName ?? 'Flash message'}
            {row?.action ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {row.action}
              </span>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {editable
              ? 'Update the text below. Changes apply immediately for end users.'
              : 'Click the pencil to edit. Changes apply immediately for end users.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2">
          <Textarea
            rows={6}
            value={draft}
            readOnly={!editable}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 resize-none"
          />
          {!editable && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setEditable(true)}
              aria-label="Edit message"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={edit.isPending}
          >
            Close
          </Button>
          {editable && (
            <Button
              type="button"
              onClick={handleSave}
              disabled={edit.isPending || unchanged}
            >
              {edit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
